import { getAssessmentUrl } from '../utils/routerUrls';
import {
  AssessmentSessionProcessTypeId,
  assessmentSessionProcessOrder,
} from '../services/exams/assessmentSession/assessments.types';
import { AssessmentSessionProcess, DashboardTile, TileType } from '../pages/Dashboard/Dashboard.types';
import { getDashboardTiles } from '../services/exams/assessmentSession/assessments.service';
import { ProcessStatus } from '../services/scoring/scoring.types';
import { ExaminerFunction } from '../pages/Assessment/Assessment.types';

const ASSESSMENT_SESSION_PROCESS_STORAGE_ID = 'AssessmentSessionProcess';

export const setAssessmentSessionProcess = (processes: AssessmentSessionProcess[]): void => {
  sessionStorage.setItem(ASSESSMENT_SESSION_PROCESS_STORAGE_ID, JSON.stringify(processes));
};

export const getAssessmentSessionProcess = (throwError = true): AssessmentSessionProcess[] => {
  const processesJson = sessionStorage.getItem(ASSESSMENT_SESSION_PROCESS_STORAGE_ID);
  if (processesJson) {
    return JSON.parse(processesJson);
  } else if (!throwError) {
    return [];
  } else {
    throw new Error('empty Assessment Session Process');
  }
};

interface ProcessLabel {
  label: string;
  letter: string;
  inProcess: string;
  toProcess: string;
  short: string;
}

export const getProcessLabel = (process?: AssessmentSessionProcessTypeId): ProcessLabel => {
  switch (process) {
    case AssessmentSessionProcessTypeId.Qualification:
      return {
        label: 'Proces kwalifikacji',
        letter: 'K',
        inProcess: 'procesie kwalifikacji',
        toProcess: 'procesu kwalifikacji',
        short: 'Kwalifikacji',
      };
    case AssessmentSessionProcessTypeId.Scoring:
      return {
        label: 'Proces oceniania',
        letter: 'O',
        inProcess: 'procesie oceniania',
        toProcess: 'procesu oceniania',
        short: 'Oceniania',
      };
    case AssessmentSessionProcessTypeId.Training:
      return {
        label: 'Proces ćwiczeniowy',
        letter: 'Ć',
        inProcess: 'procesie ćwiczeniowym',
        toProcess: 'procesu ćwiczeniowego',
        short: 'Ćwiczeniowy',
      };
    case AssessmentSessionProcessTypeId.Control:
      return {
        label: 'Proces kontrolny',
        letter: 'KO',
        inProcess: 'procesie kontrolnym',
        toProcess: 'procesu kontrolnego',
        short: 'Kontrolny',
      };
    default:
      return { label: '??', letter: '?', inProcess: '', toProcess: '???', short: '????' };
  }
};

export const getCurrentProcess = (processes: AssessmentSessionProcess[]): AssessmentSessionProcessTypeId => {
  let currentProcessId: AssessmentSessionProcessTypeId = AssessmentSessionProcessTypeId.Scoring;
  if (processes.length) {
    const foundProcess = processes.find((item: AssessmentSessionProcess) => item.isCurrentProcess);
    if (foundProcess !== undefined) {
      currentProcessId = foundProcess.assessmentSessionProcessTypeId;
    }
  }
  return currentProcessId;
};

export const getCurrentPzeAsExaminerProcess = (
  processes: AssessmentSessionProcess[],
): AssessmentSessionProcessTypeId => {
  const currentProcess =
    processes.find(
      (p) => p.isCurrentProcess && p.assessmentSessionProcessTypeId === AssessmentSessionProcessTypeId.Training,
    ) ||
    processes.find(
      (p) => p.isCurrentProcess && p.assessmentSessionProcessTypeId === AssessmentSessionProcessTypeId.Qualification,
    );

  return currentProcess?.assessmentSessionProcessTypeId ?? AssessmentSessionProcessTypeId.Training;
};

export const getLowestProcessForPzeAsExaminer = (
  processes: AssessmentSessionProcess[],
): AssessmentSessionProcessTypeId => {
  const lowestProcess = processes.reduce((lowest, current) => {
    return current.assessmentSessionProcessTypeId < lowest.assessmentSessionProcessTypeId ? current : lowest;
  });

  return lowestProcess?.assessmentSessionProcessTypeId ?? AssessmentSessionProcessTypeId.Training;
};

export const getNextProcess = (
  currentProcessId: AssessmentSessionProcessTypeId,
  processes: AssessmentSessionProcess[],
): AssessmentSessionProcessTypeId | null => {
  if (processes.length) {
    let foundCurrentProcess = false;
    const nextProcessId = assessmentSessionProcessOrder.find((type: AssessmentSessionProcessTypeId) => {
      if (type === currentProcessId) {
        foundCurrentProcess = true;
      } else if (foundCurrentProcess) {
        return true;
      }
    });
    return nextProcessId ? nextProcessId : null;
  } else {
    return null;
  }
};

export const getCurrentProcessStateLabel = (processes: AssessmentSessionProcess[]): string => {
  const currentProcess = getCurrentProcess(processes);
  return getProcessLabel(currentProcess).label;
};

export const updateSessionProcesses = async (
  assessmentSessionId: string,
  isTrainingSession?: boolean,
): Promise<DashboardTile> => {
  const newSessionData = await getDashboardTiles(assessmentSessionId, isTrainingSession);

  if (
    !newSessionData ||
    !newSessionData.data ||
    !newSessionData.data.dashboardTiles ||
    newSessionData.data.dashboardTiles.length < 1
  ) {
    throw new Error('empty Assessment Session Process data');
  }
  return newSessionData.data.dashboardTiles[0];
};

interface NextProcessUrl {
  url: string;
  nextProcess: AssessmentSessionProcessTypeId;
}

export const redirectionToProcess = async (
  assessmentSessionId: string,
  examinerId: string,
  processId: AssessmentSessionProcessTypeId | undefined,
  isTrainingSession?: boolean,
  prefetchedSession?: DashboardTile,
): Promise<NextProcessUrl> => {
  const updatedSession = prefetchedSession ?? (await updateSessionProcesses(assessmentSessionId, isTrainingSession));
  setAssessmentSessionProcess(updatedSession.assessmentSessionProcesses);
  const newProcessId =
    processId !== undefined ? processId : getCurrentProcess(updatedSession.assessmentSessionProcesses);
  return { url: getAssessmentUrl(assessmentSessionId, newProcessId.toString(), examinerId), nextProcess: newProcessId };
};

export const redirectionToNextProcess = async (
  assessmentSessionId: string,
  examinerId: string,
  isTrainingSession?: boolean,
  prefetchedSession?: DashboardTile,
): Promise<NextProcessUrl> => {
  return redirectionToProcess(assessmentSessionId, examinerId, undefined, isTrainingSession, prefetchedSession);
};

export const isProcessFinished = (
  process: AssessmentSessionProcess,
  currentProcess: AssessmentSessionProcessTypeId,
): boolean => {
  let currentWasFound = false;
  let isProcessBeforeCurrent = false;
  assessmentSessionProcessOrder.forEach((type: AssessmentSessionProcessTypeId) => {
    if (type === currentProcess) {
      currentWasFound = true;
    } else if (type === process.assessmentSessionProcessTypeId && !currentWasFound) {
      isProcessBeforeCurrent = true;
    }
  });
  return isProcessBeforeCurrent;
};

export const processIsNotAccessible = (processId: string): boolean => {
  const processType = parseInt(processId) as AssessmentSessionProcessTypeId;
  const assessmentSessionProcesses: AssessmentSessionProcess[] = getAssessmentSessionProcess(false);
  if (assessmentSessionProcesses.length === 0) {
    // If sessionStorage is empty, allow access (processes will be loaded by redirector)
    return false;
  }
  const currentProcess = getCurrentProcess(assessmentSessionProcesses);
  const findProcess: AssessmentSessionProcess | undefined = assessmentSessionProcesses.find(
    (e) => e.assessmentSessionProcessTypeId == processType,
  );
  const isProcessCompleted = findProcess ? isProcessFinished(findProcess, currentProcess) : false;

  return !isProcessCompleted && processType !== currentProcess;
};

export const isProcessEvaluationInProgres = (processStatus: ProcessStatus): boolean => {
  return processStatus === ProcessStatus.Completed;
};

export const needToNotifyAboutLeaveAssessmentProcess = (
  assessmentSession: DashboardTile | null,
  userFunction: ExaminerFunction,
): boolean => {
  return !!(
    assessmentSession?.assessmentSessionProcesses.some(
      (e) => e.assessmentSessionProcessTypeId === AssessmentSessionProcessTypeId.Scoring && e.isCurrentProcess,
    ) &&
    userFunction === ExaminerFunction.CZE &&
    assessmentSession.tileType !== TileType.PZE_RGZ
  );
};
